const APP_PIN_PREFIX = 'P';
const APP_PIN_NUMBER_LENGTH = 5;
const APP_PIN_LENGTH = 6;

/**
 * Normalize a single pin to an uppercase letter + 5 digits.
 * Examples:
 * - "p168" -> "P00168"
 * - "A1" -> "A00001"
 * - "00168" -> "P00168"
 */
const normalizeSinglePin = (pin: unknown): string | undefined => {
  if (pin === null || pin === undefined) {
    return undefined;
  }

  const raw = String(pin).trim();
  if (!raw) {
    return undefined;
  }

  const normalized = raw.toUpperCase();
  const leadLetter = normalized.match(/[A-Z]/)?.[0] || APP_PIN_PREFIX;
  const digits = normalized.replace(/\D/g, '');
  if (!digits) {
    return undefined;
  }

  const paddedDigits = digits
    .padStart(APP_PIN_NUMBER_LENGTH, '0')
    .slice(-APP_PIN_NUMBER_LENGTH);

  return `${leadLetter}${paddedDigits}`.slice(0, APP_PIN_LENGTH);
};

const isAppPinField = (key: string): boolean => {
  const normalizedKey = key.toLowerCase();
  return (
    normalizedKey === 'pin' ||
    normalizedKey === 'apppin' ||
    normalizedKey === 'app_pin' ||
    normalizedKey === 'app pin' ||
    (normalizedKey.startsWith('app') && normalizedKey.endsWith('pin')) ||
    (normalizedKey.startsWith('guardian') && normalizedKey.endsWith('pin')) ||
    (normalizedKey.startsWith('emergency') && normalizedKey.endsWith('pin'))
  );
};

const normalizePinInObject = <T extends Record<string, unknown>>(
  item: T
): T => {
  const normalized = { ...item };

  Object.keys(normalized).forEach((key) => {
    if (!isAppPinField(key)) return;

    const normalizedPin = normalizeSinglePin(normalized[key]);
    if (normalizedPin) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (normalized as any)[key] = normalizedPin;
    }
  });

  return normalized;
};

const normalizeArray = (value: unknown): unknown => {
  if (!Array.isArray(value)) {
    return value;
  }

  return value.map((item) => {
    if (item && typeof item === 'object' && !Array.isArray(item)) {
      return normalizePinInObject(item as Record<string, unknown>);
    }
    return item;
  }) as Array<unknown>;
};

export const normalizeStudentAppPins = <T extends Record<string, unknown>>(
  student: T
): T => {
  const normalized = normalizePinInObject({ ...student });

  if (
    Array.isArray((normalized as Record<string, unknown>).additionalGuardians)
  ) {
    normalized.additionalGuardians = normalizeArray(
      (normalized as Record<string, unknown>).additionalGuardians
    ) as Array<unknown>;
  }

  if (
    Array.isArray((normalized as Record<string, unknown>).emergencyContacts)
  ) {
    normalized.emergencyContacts = normalizeArray(
      (normalized as Record<string, unknown>).emergencyContacts
    ) as Array<unknown>;
  }

  if (
    (normalized as Record<string, unknown>).primaryGuardian &&
    typeof (normalized as Record<string, unknown>).primaryGuardian ===
      'object' &&
    !Array.isArray((normalized as Record<string, unknown>).primaryGuardian)
  ) {
    normalized.primaryGuardian = normalizePinInObject(
      (normalized as Record<string, unknown>).primaryGuardian as Record<
        string,
        unknown
      >
    );
  }

  return normalized;
};

export const normalizeAppPinsFromStudents = <
  T extends Array<Record<string, unknown>>,
>(
  students: T
): T => students.map((student) => normalizeStudentAppPins(student)) as T;
